import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Check, ClipboardList, LayoutGrid, PoundSterling } from 'lucide-react';
import Logo from '../components/Logo';
import { CTA_PRIMARY, MarketingFooter } from '../components/MarketingChrome';
import { getHostInfo, marketingApiBase } from '../lib/hosts';
import { useTheme } from '../context/ThemeContext';
import { MONTHLY } from '../lib/pricing';

/**
 * The pre-launch homepage for holidayhive.co.uk — Rob's option A, 12/09/2026.
 *
 * Shown on the apex hosts listed in `lib/hosts.js#PRELAUNCH_HOSTS` instead of the full
 * landing page, because the app is not served on the brand domain yet. It does three
 * things and nothing else: says what HolidayHive is, asks for an email, and carries what
 * Google's OAuth reviewer reads a homepage for (what the app does, what it does with a
 * connected calendar — the one sentence under "Syncs with your calendar" — and visible
 * Privacy / Terms links in the shared footer). No button on this page goes nowhere.
 *
 * The form posts to the SAME origin (`marketingApiBase()`), so it works on
 * holidayhive.co.uk without that host being in the backend's CORS list.
 */

const features = [
  {
    icon: Check,
    title: 'Requests and approvals in one click',
    text: 'Staff ask, managers approve — by email or in Microsoft Teams.',
  },
  {
    icon: LayoutGrid,
    title: 'A shared wallchart',
    text: "Who's off, when, across every team — no spreadsheet.",
  },
  {
    icon: CalendarDays,
    title: 'Syncs with your calendar',
    // ⚠️ This sentence is load-bearing for Google OAuth verification (GOOGLE-VERIFICATION.md):
    // the homepage must say what the app does with Google user data. Keep it true.
    text: 'Optional. If you connect Google or Outlook, we only add your approved leave to your own calendar — and never read anything else.',
  },
  {
    icon: PoundSterling,
    title: 'Payroll-ready reports',
    text: 'Absence by pay period, without the reasons.',
  },
];

const PrelaunchPage = () => {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot — never shown, never filled by a person
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [error, setError] = useState('');

  // Always light. This page paints its own cream background, so a visitor whose phone is
  // set to dark mode would otherwise get the "Holiday" half of the wordmark in white on
  // cream — i.e. the logo reads "Hive" (Rob, 12/09/2026). No theme toggle here either.
  const { setTheme } = useTheme();
  useEffect(() => { setTheme('light'); }, [setTheme]);

  const submit = async (e) => {
    e.preventDefault();
    if (state === 'sending') return;
    setState('sending');
    setError('');
    try {
      const res = await fetch(`${marketingApiBase()}/api/interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, website }),
      });
      if (res.ok) {
        setState('done');
        return;
      }
      let detail = '';
      try { detail = (await res.json()).detail || ''; } catch (_) { /* not JSON */ }
      setError(typeof detail === 'string' && detail ? detail : 'Something went wrong — please try again.');
      setState('error');
    } catch (_) {
      setError("We couldn't reach the server — please try again in a moment.");
      setState('error');
    }
  };

  return (
    <div className="min-h-screen bg-hive-warm font-nunito text-hive-navy">
      <nav className="mx-auto flex max-w-[1240px] items-center gap-7 px-8 py-[22px]">
        <a href={getHostInfo().marketingOrigin} className="inline-flex" data-testid="logo">
          <Logo size={32} />
        </a>
        <div className="hidden gap-6 font-poppins text-[14.5px] font-medium text-hive-muted md:flex">
          <a href="#what" className="transition-colors hover:text-hive-navy">What it does</a>
          <Link to="/privacy" className="transition-colors hover:text-hive-navy">Privacy</Link>
        </div>
      </nav>

      <section className="mx-auto max-w-[760px] px-8 pb-12 pt-10 text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-hive-pending-bg px-3.5 py-1.5 text-[12.5px] font-bold text-hive-pending-fg">
          <span className="h-1.5 w-1.5 rounded-full bg-hive-orange" aria-hidden />
          Launching soon
        </span>
        <h1 className="m-0 mt-5 font-poppins text-4xl font-bold leading-[1.15] sm:text-5xl lg:text-[54px]">
          The friendliest <span className="text-hive-orange">staff holiday</span> and{' '}
          <span className="text-hive-bh">absence</span> tracker
        </h1>
        <p className="mx-auto mt-6 max-w-[560px] text-[18px] leading-[1.65] text-hive-muted">
          HolidayHive is nearly ready. Leave your email and you'll be first to hear when it
          opens — one email, no spam, and you can ask us to forget it any time.
        </p>

        {state === 'done' ? (
          <div
            className="mx-auto mt-8 max-w-[440px] rounded-[18px] border border-hive-line bg-white px-6 py-6"
            role="status"
            data-testid="interest-done"
          >
            <div className="font-poppins text-lg font-bold">You're on the list</div>
            <p className="mt-2 text-[15px] leading-[1.6] text-hive-muted">
              Thanks — we'll email you when HolidayHive opens. Nothing else to do.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="relative mx-auto mt-8 max-w-[440px] text-left" noValidate>
            <label htmlFor="interest-email" className="block text-[11px] font-bold uppercase tracking-[.08em] text-hive-muted">
              Work email
            </label>
            <div className="mt-1.5 flex flex-col gap-2.5 sm:flex-row">
              <input
                id="interest-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourcompany.co.uk"
                className="h-12 flex-1 rounded-full border border-hive-line bg-white px-5 text-[15px] text-hive-navy outline-none placeholder:text-hive-muted-cool focus:border-hive-orange focus:ring-2 focus:ring-hive-orange/30"
                data-testid="interest-email"
              />
              <button
                type="submit"
                disabled={state === 'sending'}
                className={`${CTA_PRIMARY} h-12 px-7 text-[15px] shadow-hive-cta disabled:opacity-60`}
                data-testid="interest-submit"
              >
                {state === 'sending' ? 'Sending…' : 'Register my interest'}
              </button>
            </div>
            {/* Honeypot: bots fill every field; people never see this one. */}
            <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor="interest-website">Website</label>
              <input id="interest-website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
            </div>
            {state === 'error' && (
              <p className="mt-2.5 text-[13.5px] font-semibold text-hive-danger-fg" role="alert">{error}</p>
            )}
            <p className="mt-3 text-center text-[13px] font-bold text-hive-muted">
              {MONTHLY} per person per month at launch
            </p>
          </form>
        )}
      </section>

      <section id="what" className="scroll-mt-8 bg-hive-cream px-8 py-14">
        <div className="mx-auto max-w-[880px]">
          <h2 className="m-0 flex items-center gap-3 font-poppins text-2xl font-bold">
            <ClipboardList className="h-6 w-6 text-hive-orange" aria-hidden />
            What HolidayHive does
          </h2>
          <div className="mt-7 grid gap-6 sm:grid-cols-2">
            {features.map(({ icon: Icon, title, text }) => (
              <div key={title} className="flex gap-4">
                <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-[12px] bg-hive-pending-bg text-hive-orange">
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <div>
                  <div className="font-poppins text-[16px] font-semibold">{title}</div>
                  <p className="mt-1 text-[14.5px] leading-[1.6] text-hive-muted">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter legalOnly />
    </div>
  );
};

export default PrelaunchPage;
